'use client'

import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {ShopperExperience} from 'commerce-sdk-isomorphic'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {USEQUERY_OPTIONS} from '../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

/**
 * Hook: usePageDesigner
 * Fetches Page Designer content from SCAPI via React Query.
 * Supports both standard pages and product/category-driven (PLP/PDP) pages.
 */
const usePageDesigner = (parameters = {}, options = {}) => {
    const appOrigin = useAppOrigin()
    const {getTokenWhenReady} = useAccessToken()

    const {
        app: {commerceAPI}
    } = getConfig()

    const {parameters: scapiParams} = commerceAPI
    const {pageId, isPlp = false, isPdp = false, pageDesigner} = parameters

    /**
     * Fetch Page Designer data from SCAPI
     */
    const getPageDesigner = async () => {
        try {
            const token = await getTokenWhenReady()

            // Initialize SCAPI client
            const shopperExperienceClient = new ShopperExperience({
                proxy: `${appOrigin}/api/caching/`,
                headers: {Authorization: `Bearer ${token}`},
                parameters: scapiParams
            })

            // PLP/PDP pages use getPages()
            if (isPlp || isPdp) {
                const {aspectTypeId, categoryId, aspectAttributes} = pageDesigner || {}
                return await shopperExperienceClient.getPages({
                    parameters: {categoryId, aspectTypeId, aspectAttributes}
                })
            }

            // Standard pages use getPage()
            return await shopperExperienceClient.getPage({
                parameters: {pageId}
            })
        } catch (error) {
            logger.error('[usePageDesigner] Failed to fetch Page Designer data:', error.message)
            throw error
        }
    }

    return useQuery({
        queryKey: ['page-designer', parameters],
        queryFn: getPageDesigner,
        ...USEQUERY_OPTIONS,
        staleTime: 60 * 1000, // cache for 1 minute
        enabled: Boolean(options?.enabled)
    })
}

export default usePageDesigner
